/**
 * The intervention log — every lever pull, with its before, after and reason
 * (product brief §5, and the surface journey J4 drills into).
 *
 * The brief calls this a first-class surface rather than an audit afterthought,
 * and the reason is in the failure it prevents: CRM forecast tools go wrong when
 * rep overrides become invisible top-down edits, and the fix is that anyone can
 * read what was changed and why without asking. So this is built to be *read* —
 * a dated stream in the shape of a conversation, not a grid of columns.
 *
 * Every entry carries the same four things: what moved, from what to what, why,
 * and how much of the month it was worth. The Tharstern marker on the entries
 * that wrote back matters to a manager reconciling with the master record: it
 * separates the changes that left our system from the ones that did not.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  Inbox,
  Percent,
  TrendingUp,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  InterventionDaySummary,
  InterventionLogEntry,
  InterventionLogResponse,
  LoggedLever,
} from "@shared-core/data/my-pipeline-types";
import {
  formatCurrencyGbpCompact,
  formatLongWeekdayDateOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { MATERIAL_MOVE_PCT } from "@shared-core/domain/my-pipeline-interventions";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";

const LEVER_LABEL: Record<LoggedLever, string> = {
  estimate_status: "Status",
  quote_conversion: "Conversion",
  expected_date: "Expected date",
  unseen_projection: "Unseen work",
  arrival_resolution: "Arrival settled",
  quote_reviewed: "Reviewed",
};

const LEVER_ICON: Record<LoggedLever, LucideIcon> = {
  estimate_status: CheckCircle2,
  quote_conversion: Percent,
  expected_date: CalendarClock,
  unseen_projection: TrendingUp,
  arrival_resolution: Inbox,
  quote_reviewed: CheckCircle2,
};

/** Filters worth having on a stream people scan. Not a query builder. */
type LeverFilter = "all" | LoggedLever;

export function InterventionLogPage() {
  const adapter = useDataAdapter();
  const [params] = useSearchParams();
  const repId = params.get("rep") ?? undefined;

  const [log, setLog] = useState<InterventionLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lever, setLever] = useState<LeverFilter>("all");
  const [materialOnly, setMaterialOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const result = await adapter.getInterventionLog(repId);
        if (!cancelled) setLog(result);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load the log",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, repId]);

  /**
   * Grouped by rep and day, because that is the unit the alert fires on: one
   * digest per rep per day, netted, measured against their unbooked total.
   * Grouping the log the same way means the screen a manager lands on from an
   * alert is shaped like the alert that sent them.
   */
  const groups = useMemo(() => {
    const summaries = log?.days ?? [];
    const wanted = materialOnly
      ? summaries.filter((d) => d.material)
      : summaries;
    return wanted
      .map((day) => ({
        day,
        entries: (log?.entries ?? []).filter(
          (e) =>
            e.rep_id === day.rep_id &&
            e.at.slice(0, 10) === day.date &&
            (lever === "all" || e.lever === lever),
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [log, lever, materialOnly]);

  if (loading) {
    return (
      <PageFrame>
        <div className="h-40 animate-pulse rounded-lg border border-border bg-surface-subtle" />
      </PageFrame>
    );
  }

  if (error || !log) {
    return (
      <PageFrame>
        <Card className="p-6">
          <h2 className="font-semibold">Could not load the log</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {error ?? "No data returned."}
          </p>
        </Card>
      </PageFrame>
    );
  }

  const levers: LeverFilter[] = [
    "all",
    "estimate_status",
    "quote_conversion",
    "expected_date",
    "unseen_projection",
    "arrival_resolution",
  ];

  return (
    <PageFrame>
      <header>
        <Link
          to={paths.myPipeline()}
          className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          My Pipeline
        </Link>
        <h1 className="text-xl font-semibold">Intervention log</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          {log.rep_name ? `${log.rep_name} · ` : "Everyone · "}
          {log.month_label}. Every change anyone made to the forecast, what it
          was before, and why. Grouped by rep and day &mdash; the same unit the
          manager&rsquo;s alert fires on.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-1.5">
        {levers.map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={lever === l}
            onClick={() => setLever(l)}
            className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
              lever === l
                ? "bg-primary/10 font-semibold text-primary"
                : "text-muted-foreground hover:bg-surface-hover"
            }`}
          >
            {l === "all" ? "Everything" : LEVER_LABEL[l]}
          </button>
        ))}
        <Button
          size="sm"
          variant={materialOnly ? "default" : "outline"}
          className="ml-auto"
          onClick={() => setMaterialOnly((m) => !m)}
        >
          {Math.round(MATERIAL_MOVE_PCT * 100)}%+ days only
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
          {log.entries.length === 0
            ? `Nobody has touched ${log.rep_name ? `${log.rep_name}\u2019s forecast` : "the forecast"} this month \u2014 the number is entirely the system\u2019s.`
            : "Nothing matches that filter."}
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(({ day, entries: dayEntries }) => (
            <section key={`${day.date}|${day.rep_id}`} className="space-y-2">
              <DayHeading day={day} showRep={!log.rep_id} />
              <ul className="space-y-2">
                {dayEntries.map((entry) => (
                  <LogRow key={entry.id} entry={entry} showRep={!log.rep_id} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4 pb-20">
      <WorkspaceNav active="pipeline" />
      {children}
    </div>
  );
}

/**
 * The day's heading is where the alert lives, because the alert *is* a day:
 * one per rep, netted, against their unbooked total. Stating the net beside
 * the date is what stops a reader adding up the rows themselves and getting a
 * different answer from the bell.
 */
function DayHeading({
  day,
  showRep,
}: {
  day: InterventionDaySummary;
  showRep: boolean;
}) {
  const date = formatLongWeekdayDateOrDash(day.date);
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {date}
        {showRep && ` \u00b7 ${day.rep_name}`}
      </h2>
      <span className="text-xs text-muted-foreground">
        net{" "}
        <span className="font-medium tabular-nums text-foreground">
          {day.net_delta === 0
            ? "no change"
            : `${day.net_delta > 0 ? "+" : "\u2212"}${formatCurrencyGbpCompact(Math.abs(day.net_delta))}`}
        </span>
        {/* Suppressed when it rounds to nothing: "0% of unbooked" is noise on
            a day whose changes were housekeeping. */}
        {Math.round(day.share * 100) > 0 && (
          <>
            {" "}
            {"\u00b7"} {Math.round(day.share * 100)}% of unbooked
          </>
        )}
      </span>
      {day.material && (
        <Badge variant={day.alerted ? "warning" : "neutral"}>
          <TrendingUp className="size-3" />
          {day.alerted ? "manager alerted" : "alerts when today closes"}
        </Badge>
      )}
    </div>
  );
}

function LogRow({
  entry,
  showRep,
}: {
  entry: InterventionLogEntry;
  showRep: boolean;
}) {
  const Icon = LEVER_ICON[entry.lever];
  return (
    <li>
      <Card className="p-3">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Badge variant="neutral">{LEVER_LABEL[entry.lever]}</Badge>
              <Link
                to={entry.subject_url}
                className="font-mono text-xs text-primary hover:underline"
              >
                {entry.subject_label}
              </Link>
              <span className="truncate text-sm">{entry.subject_detail}</span>
            </div>

            {/* Before → after, the sentence the log exists to hold. */}
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              <span>{entry.before}</span>
              <ArrowRight className="size-3.5 shrink-0" />
              <span className="font-medium text-foreground">{entry.after}</span>
              {entry.wrote_back_to_tharstern && (
                <span
                  className="inline-flex items-center gap-1 text-xs"
                  title="This change was written back to Tharstern"
                >
                  <ExternalLink className="size-3" />
                  written to Tharstern
                </span>
              )}
            </div>

            {(entry.reason_code || entry.note) && (
              <p className="mt-1 text-sm">
                {entry.reason_code && (
                  <span className="font-medium">{entry.reason_code}</span>
                )}
                {entry.reason_code && entry.note && " — "}
                {entry.note && (
                  <span className="italic text-muted-foreground">
                    &ldquo;{entry.note}&rdquo;
                  </span>
                )}
              </p>
            )}

            <p className="mt-1 text-xs text-muted-foreground">
              {showRep && (
                <>
                  <Users className="mr-1 inline size-3" />
                  {entry.rep_name} ·{" "}
                </>
              )}
              {/* The actor is normally the rep themselves, and naming them
                  twice reads as a bug. Name them alongside the rep only when
                  someone else pulled the lever on that rep's book — which is
                  precisely the case a manager wants to see. */}
              {(!showRep || entry.actor_name !== entry.rep_name) && (
                <>{entry.actor_name} · </>
              )}
              {formatTimeOrDash(entry.at)} · {entry.client_name}
            </p>
          </div>

          <div className="shrink-0 text-right">
            <div
              className={`font-semibold tabular-nums ${
                entry.month_delta === 0 ? "text-muted-foreground" : ""
              }`}
            >
              {entry.month_delta === 0
                ? "no change"
                : `${entry.month_delta > 0 ? "+" : "−"}${formatCurrencyGbpCompact(Math.abs(entry.month_delta))}`}
            </div>
            <div className="text-xs text-muted-foreground">to the month</div>
          </div>
        </div>
      </Card>
    </li>
  );
}
